import { useQuery } from '@tanstack/react-query'

import { v2Api } from './api'
import { ErrorBanner, Spinner } from './components/Feedback'
import { Avatar } from './components/IssueBadges'
import { STATUS_LABELS } from './filters'
import { issueKeys } from './keys'
import { relativeTime } from './lib/ui'
import type { ActivityEntry, TaskStatus } from './types'

/** Every action string the v2 handlers write, in past tense. */
const ACTION_TEXT: Record<string, string> = {
  'issue.created': 'created this issue',
  'issue.updated': 'updated this issue',
  'issue.moved': 'moved this issue',
  'issue.deleted': 'deleted this issue',
  'issue.bulk_updated': 'updated this issue in bulk',
  'issue.labels_updated': 'changed the labels',
  'comment.created': 'commented',
  'comment.updated': 'edited a comment',
  'comment.deleted': 'deleted a comment',
  'sprint.created': 'created a sprint',
  'sprint.updated': 'updated the sprint',
  'sprint.started': 'started the sprint',
  'sprint.completed': 'completed the sprint',
  'sprint.deleted': 'deleted the sprint',
  'sprint.scope_changed': 'changed the sprint scope',
  'board.columns_updated': 'changed the board columns',
}

const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  priority: 'Priority',
  title: 'Summary',
  description: 'Description',
  assignee_id: 'Assignee',
  reporter_id: 'Reporter',
  sprint_id: 'Sprint',
  column_id: 'Column',
  story_points: 'Story points',
  due_date: 'Due date',
  issue_type: 'Type',
  parent_id: 'Parent',
  labels: 'Labels',
  rank: 'Order',
}

function readable(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'none'
  if (field === 'status' && typeof value === 'string') {
    return STATUS_LABELS[value as TaskStatus] ?? value
  }
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'none'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function changeLines(entry: ActivityEntry): string[] {
  const changes = entry.changes
  if (!changes) return []
  const lines: string[] = []
  for (const [field, raw] of Object.entries(changes)) {
    const label = FIELD_LABELS[field] ?? field
    if (raw && typeof raw === 'object' && ('from' in raw || 'to' in raw)) {
      const { from, to } = raw as { from?: unknown; to?: unknown }
      if (readable(field, from) === readable(field, to)) continue
      lines.push(`${label}: ${readable(field, from)} → ${readable(field, to)}`)
    } else if (field !== 'sprint_id' || typeof raw !== 'string') {
      lines.push(`${label}: ${readable(field, raw)}`)
    }
  }
  return lines
}

/**
 * The audit trail for one issue, newest first.
 *
 * Status and column moves are additionally written to `task_status_history` on
 * the server, so cycle-time reporting never has to reconstruct state from these
 * JSON diffs — this view is for people, not for maths.
 */
export function ActivityFeed({ slug, issueRef }: { slug: string | null; issueRef: string }) {
  const activity = useQuery({
    queryKey: issueKeys.activity(slug ?? '', issueRef),
    queryFn: () => v2Api.activity(slug as string, issueRef),
    enabled: Boolean(slug && issueRef),
    select: (response) => response.data,
  })

  if (activity.isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-ink-500">
        <Spinner /> Loading history…
      </div>
    )
  }
  if (activity.error) {
    return <ErrorBanner error={activity.error} onRetry={() => void activity.refetch()} />
  }
  if (!activity.data || activity.data.length === 0) {
    return <p className="py-2 text-sm text-ink-500">Nothing has happened here yet.</p>
  }

  return (
    <ol className="space-y-3">
      {activity.data.map((entry) => {
        const lines = changeLines(entry)
        const actor = entry.actor?.name ?? entry.actor_label ?? 'Someone'
        return (
          <li key={entry.id} className="flex gap-2.5">
            <Avatar user={entry.actor ?? null} size="xs" className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink-700 dark:text-ink-200">
                <span className="font-medium text-ink-800 dark:text-ink-100">{actor}</span>
                {entry.actor_kind === 'token' && (
                  <span className="ml-1 rounded bg-ink-100 px-1 text-[10px] font-medium uppercase tracking-wide text-ink-500 dark:bg-ink-800">
                    agent
                  </span>
                )}{' '}
                {ACTION_TEXT[entry.action] ?? entry.action.replace(/[._]/g, ' ')}{' '}
                <span className="text-xs text-ink-500">{relativeTime(entry.created_at)}</span>
              </p>
              {lines.length > 0 && (
                <ul className="mt-0.5 space-y-0.5">
                  {lines.map((line) => (
                    <li key={line} className="text-xs text-ink-500">
                      {line}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
